import { RenewalEvent, RenewalStatusState } from '../models/ResourceModels';

export function getRenewalStatusDisplay(state?: RenewalStatusState): { label: string; icon: 'initiated' | 'progress' | 'success' | 'failed' | 'superseded' } {
  switch (state) {
    case 'TRIGGERED':
      return { label: 'Renewal triggered', icon: 'initiated' };
    case 'INITIATED':
      return { label: 'Renewal initiated', icon: 'initiated' };
    case 'IN_PROGRESS':
      return { label: 'Renewal in progress', icon: 'progress' };
    case 'SUCCESS':
      return { label: 'Renewal succeeded', icon: 'success' };
    case 'FAILED':
      return { label: 'Renewal failed', icon: 'failed' };
    case 'SUPERSEDED':
      return { label: 'Renewal superseded', icon: 'superseded' };
    default:
      return { label: '', icon: 'initiated' };
  }
}

export function isRenewalStatusTerminal(state?: RenewalStatusState): boolean {
  return state === 'SUCCESS' || state === 'FAILED' || state === 'SUPERSEDED';
}

// Renewal-status timestamps arrive as ISO-8601 strings with sub-millisecond precision
// (e.g. nanoseconds), which is only implementation-defined per the ECMAScript Date
// string format beyond 3 fractional digits. Truncate before parsing/displaying.
export function formatRenewalTimestamp(timestamp?: string | null): string {
  if (!timestamp) {
    return '';
  }
  const truncated = timestamp.replace(/(\.\d{3})\d+(Z)$/, '$1$2');
  const date = new Date(truncated);
  if (isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

export interface ReducedRenewalStatus {
  latest: RenewalEvent | undefined;
  state: RenewalStatusState | undefined;
  isSuperseded: boolean;
}

// The events array is the source of truth for the full timeline (the stepper), but the
// pill and the modal header only need "what's the current state" — derived here so both
// call sites agree on the same notion of "latest" (sorted defensively; the backend does
// not guarantee array order).
export function reduceRenewalEvents(events?: RenewalEvent[]): ReducedRenewalStatus {
  if (!events || events.length === 0) {
    return { latest: undefined, state: undefined, isSuperseded: false };
  }
  const sorted = [...events].sort((a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime());
  const latest = sorted[sorted.length - 1];
  return { latest, state: latest.state, isSuperseded: latest.state === 'SUPERSEDED' };
}

export type StepperNodeStatus = 'active' | 'complete' | 'failed' | 'superseded';

export interface StepperNode {
  key: string;
  label: string;
  state?: RenewalStatusState;
  recordedAt?: string | null;
  status: StepperNodeStatus;
  message?: string | null;
  reasonCode?: string | null;
}

const NON_TERMINAL_STAGE_ORDER: RenewalStatusState[] = ['TRIGGERED', 'INITIATED', 'IN_PROGRESS'];
const STAGE_LABELS: Record<'TRIGGERED' | 'INITIATED' | 'IN_PROGRESS' | 'SUPERSEDED', string> = {
  TRIGGERED: 'Triggered',
  INITIATED: 'Initiated',
  IN_PROGRESS: 'In Progress',
  SUPERSEDED: 'Superseded'
};

// Maps the raw event list into the node list the stepper renders: a 2-node
// Triggered->Superseded layout for a superseded attempt (it never reaches "In
// progress"), or a Triggered/Initiated/In progress/Completed layout otherwise. A
// stage with no matching event (e.g. no TRIGGERED event on an auto-initiated
// renewal, per the design doc, or a stage never reached because the renewal
// short-circuited straight to a terminal state) is omitted entirely rather than
// rendered as an indefinite "Waiting" placeholder.
export function getStepperNodes(events?: RenewalEvent[]): StepperNode[] {
  const byState = new Map<RenewalStatusState, RenewalEvent>();
  (events ?? []).forEach(event => byState.set(event.state, event));
  const { latest, isSuperseded } = reduceRenewalEvents(events);

  if (isSuperseded) {
    const nodes: StepperNode[] = [];
    const triggered = byState.get('TRIGGERED');
    if (triggered) {
      nodes.push({ key: 'TRIGGERED', label: STAGE_LABELS.TRIGGERED, state: 'TRIGGERED', recordedAt: triggered.recordedAt, status: 'complete' });
    }
    const superseded = byState.get('SUPERSEDED');
    nodes.push({
      key: 'SUPERSEDED',
      label: STAGE_LABELS.SUPERSEDED,
      state: 'SUPERSEDED',
      recordedAt: superseded?.recordedAt ?? null,
      status: 'superseded',
      message: superseded?.message ?? 'Another renewal on this account took over.'
    });
    return nodes;
  }

  const isTerminal = !!latest && isRenewalStatusTerminal(latest.state);
  const reachedIndex = latest ? NON_TERMINAL_STAGE_ORDER.indexOf(latest.state) : -1;

  const nodes: StepperNode[] = [];
  NON_TERMINAL_STAGE_ORDER.forEach((state, index) => {
    const event = byState.get(state);
    if (!event) {
      return;
    }
    const status: StepperNodeStatus = index === reachedIndex && !isTerminal ? 'active' : 'complete';
    nodes.push({ key: state, label: STAGE_LABELS[state], state, recordedAt: event.recordedAt, status });
  });

  if (isTerminal && latest) {
    nodes.push({
      key: 'COMPLETED',
      label: latest.state === 'FAILED' ? 'Failed' : 'Success',
      state: latest.state,
      recordedAt: latest.recordedAt,
      status: latest.state === 'FAILED' ? 'failed' : 'complete',
      message: latest.message ?? null,
      reasonCode: latest.reasonCode ?? null
    });
  }

  return nodes;
}
